import * as React from "react"
import { FC, useEffect, useState } from "react"
import { AdminUserProps, TableColumn, User } from "../../../../types"
import adminUserColumns from "./columns/adminUserColumns"
import { connect } from "react-redux"
import {
  fetchAllUsers,
  fetchLastUpdate,
  refreshLabelCamp,
  refreshReports,
  refreshSummaries,
  selectUser
} from "../../../../actions/adminActions"
import { RootState } from "../../../../store"
import { ADMIN_PERMISSIONS, adminTableLimit, USER_MEMBER_TYPE, USER_ROLE, userTypeOptions } from "../../../../constants"
import { KButton, KInput, KSpan, KTitleSpan, KTooltip, KDropdown } from "kahuna-base-react-components"
import TableNew from "../../../ui/table/TableNew"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import ReloadModal from "./modals/ReloadModal"
// @ts-ignore
import PlusIcon from "../../../../assets/release-registry-icons/plus.svg"
import AdminUserCreateModal from "./modals/AdminUserCreateModal"
import { AdminUserAssetTabOptions } from "../AdminUserAsset"
import AdminNewLabelModal from "./modals/AdminNewLabelModal"
import { checkAdminPermission } from "../../../../utility"
import AdminUserLog from "./AdminUserLog"
import AdminTakedownLog from "./takedown-logs/AdminTakedownLog"
import AdminNotificationWatch from "./AdminNotificationWatch"
import AdminAnnouncementWatch from "./AdminAnnouncementWatch"

enum PageOptions {
  USERS,
  USER_LOGS,
  TAKEDOWN_LOGS,
  NOTIFICATIONS,
  ANNOUNCEMENTS
}

const AdminUser: FC<AdminUserProps> = (props) => {
  const [loading, setLoading] = useState(true)
  const [timeoutId, setTimeoutId] = useState(undefined)
  const [inputTimeoutId, setInputTimeoutId] = useState(undefined)
  const [openSidebar, setOpenSidebar] = useState<boolean>(true)
  const [selectedPage, setSelectedPage] = useState<number>(PageOptions.USERS)

  const [pageNumber, setPageNumber] = useState<number>(Math.max(1, Number(props.params.get("page") || 1)))
  const [searchValue, setSearchValue] = useState<string>("")
  const searchOptions: KSelectOption[] = [
    { label: "User Name", value: 0, value2: "username" },
    { label: "First Name", value: 1, value2: "first_name" },
    { label: "Last Name", value: 2, value2: "last_name" }
  ]
  const [selectedSearchOption, setSelectedSearchOption] = useState<KSelectOption>(searchOptions[0])
  const [additionalFilters, setAdditionalFilters] = useState<{ [key: string]: string | undefined }>({})
  const [hoveredRowId, setHoveredRowId] = useState<number | undefined>(undefined)
  const [newLabelModal, setNewLabelModal] = useState(false)

  const [reloadModalOpen, setReloadModalOpen] = useState<boolean>(false)
  const [addEditUserModal, setAddEditUserModalOpen] = useState<boolean>(false)
  const [users, setUsers] = useState<User[]>([])
  const [hideMembers, setHideMembers] = useState<boolean>(false)
  const [userRolesOpen, setUserRolesOpen] = useState(false)

  const adminUserCols: TableColumn[] = adminUserColumns(
    checkAdminPermission(ADMIN_PERMISSIONS.USER_ASSET_WRITE, props.loggedInUser?.adminPermissions),
    props.setModalOption,
    props.selectUser,
    setAddEditUserModalOpen
  )
  const [columns, setColumns] = useState<TableColumn[]>(adminUserCols)

  const roleParamToState = () => {
    const roleParam = props.params.get("role")
    if (!roleParam) return []

    const roles = roleParam.split(",")

    const result: number[] = []

    roles.forEach((role) => {
      const opt = userTypeOptions.find((option) => option?.value2 === role)
      if (opt) result.push(opt.value)
    })

    return result
  }

  const [selectedUserTypes, setSelectedUserTypes] = useState<number[]>(() => roleParamToState())

  useEffect(() => {
    if (
      props.params.has("log_id") ||
      props.params.has("log_upc") ||
      props.params.has("log_user_id") ||
      props.params.has("audit_user_id")
    ) {
      setSelectedPage(PageOptions.USER_LOGS)
    }
  }, [props.params])

  useEffect(() => {
    const results = props.paginatedUsers.results
    const processedUsers: User[] = []
    results.forEach((user) => {
      processedUsers.push(user)
      if (hideMembers) return

      user.userMembers?.forEach((member) => {
        processedUsers.push({ ...member, isUserMember: true })
      })
    })
    setUsers(processedUsers)
  }, [props.paginatedUsers, hideMembers])

  const stateToRoleParam = (values: number[]) => {
    if (values.length === 0) return ""

    const parts = values
      .map((value) => {
        const opt = userTypeOptions.find((option) => option.value === value)
        return opt ? opt.value2 : null
      })
      .filter(Boolean) as string[]
    return parts.join(",")
  }

  useEffect(() => {
    props.setParams((prev) => {
      const obj = Object.fromEntries(prev) as Record<string, string>

      delete obj.user_id

      obj.page = String(pageNumber)

      const roleStr = stateToRoleParam(selectedUserTypes)
      if (roleStr) obj.role = roleStr
      else delete obj.role

      return obj
    })
  }, [pageNumber, selectedUserTypes])

  const onClickPage = async (
    pageNumber: number,
    filters: { [key: string]: string | number | boolean } | undefined,
    sortField?: string,
    sortDirection?: "asc" | "desc"
  ) => {
    setLoading(true)
    await props.fetchAllUsers(adminTableLimit, (pageNumber - 1) * adminTableLimit, filters, sortField, sortDirection)
    setLoading(false)
    setPageNumber(pageNumber)
  }

  const onSortOrFilterChange = async (
    filters: { [key: string]: string | number | boolean },
    sortField: string,
    sortDirection?: "asc" | "desc",
    debounce?: boolean
  ) => {
    // runs in first render (mount) without applying any filter or sort param
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    setTimeoutId(
      //@ts-ignore
      setTimeout(
        async () => {
          const page = pageNumber
          const offset = (page - 1) * adminTableLimit
          setLoading(true)
          await props.fetchAllUsers(adminTableLimit, offset, filters, sortField, sortDirection)
          setLoading(false)
        },
        debounce ? 500 : 0
      )
    )
  }

  useEffect(() => {
    clearTimeout(inputTimeoutId)
    setInputTimeoutId(
      // @ts-ignore
      setTimeout(async () => {
        if (selectedSearchOption?.value2) {
          setAdditionalFilters({
            username: selectedSearchOption?.value2 === "username" ? searchValue : "",
            first_name: selectedSearchOption?.value2 === "first_name" ? searchValue : "",
            last_name: selectedSearchOption?.value2 === "last_name" ? searchValue : "",
            role__id: selectedUserTypes.length > 0 ? selectedUserTypes.join(",") : undefined
          })
        }
      }, 500)
    )
  }, [selectedSearchOption, searchValue, selectedUserTypes])

  const onClickAdd = () => {
    props.selectUser(new User())
    setAddEditUserModalOpen(true)
  }

  const onSelectRow = (index: number) => {
    if (index === -1) return // onClickPagination triggers onSelectRow with index -1, so I cancelled it
    const clickedUser = users[index]
    props.selectUser(clickedUser)
    props.setTab(AdminUserAssetTabOptions.ASSET)

    props.setParams({ user_id: String(clickedUser.id) })
  }

  const topRightElements = (
    <div className="flex flex-row items-center gap-3">
      <div className="cursor-pointer" onClick={() => setHideMembers(!hideMembers)}>
        <KTooltip
          position="bottom"
          children={<img src={hideMembers ? "/account_icons/eye-closed.svg" : "/account_icons/eye-open.svg"} />}
          content={<KSpan text={hideMembers ? "Show Members" : "Hide Members"} />}
        />
      </div>
      <div
        className="flex items-center py-[13px] pl-3.5 pr-3 gap-2 rounded-[10px] w-[300px]"
        style={{
          background: searchValue ? "#FFF" : "#F7F7F7",
          border: "1px solid #F3F3F3",
          height: "48px",
          transition: "0.3s"
        }}
      >
        <KInput
          dataTestId="search-input"
          value={searchValue}
          padding="0px"
          background="transparent"
          onChange={(text: string) => {
            setSearchValue(text)
            setPageNumber((prev) => (prev === 1 ? prev : 1)) // Always 1
          }}
          leftIcon="/catalog_icons/search-grey.svg"
          gap="8px"
          borderRadius={0}
          shadowDisabled
          placeholder={"Search..."}
        />
        <KDropdown
          rightIcon="/admin_icons/caret-down.svg"
          options={searchOptions}
          selected={selectedSearchOption}
          onSelect={(selected) => setSelectedSearchOption(selected as KSelectOption)}
          padding="0px"
          background="transparent"
          activeBackground="transparent"
          shadowDisabled={true}
          enableIndicator={true}
          menuWidth={140}
          width={140}
          menuLeftMargin={-40}
          textColor="#F3F3F3"
        />
      </div>
      {checkAdminPermission(ADMIN_PERMISSIONS.USER_ASSET_WRITE, props.loggedInUser?.adminPermissions) && (
        <div className="flex">
          <KButton
            leftIcon={PlusIcon}
            background="#000"
            hoverBackground="#111"
            activeBackground="#000"
            onClick={() => onClickAdd()}
            width="auto"
            height="48px"
            padding="14px"
            text="New User"
            textColor="#FFF"
            gap="4px"
          />
        </div>
      )}

      {checkAdminPermission(ADMIN_PERMISSIONS.USER_ASSET_WRITE, props.loggedInUser?.adminPermissions) && (
        <div className="flex">
          <KButton
            leftIcon={PlusIcon}
            background="#000"
            hoverBackground="#111"
            activeBackground="#000"
            onClick={() => setNewLabelModal(true)}
            width="auto"
            height="48px"
            padding="14px"
            text="New Label"
            textColor="#FFF"
            gap="4px"
          />
        </div>
      )}
      {checkAdminPermission(ADMIN_PERMISSIONS.USER_ASSET_RELOAD_REPORT, props.loggedInUser?.adminPermissions) && (
        <div className="flex">
          <KButton
            text="Reload Reports"
            height="48px"
            width="140px"
            background="#F7F7F7"
            hoverBackground="#F3F3F3"
            padding="14px"
            onClick={() => setReloadModalOpen(true)}
          />
        </div>
      )}
    </div>
  )

  const tableTitle = (title: string) => (
    <div className="flex flex-row gap-2 items-center !cursor-default">
      <KTooltip
        backgroundColor="#000"
        padding="0px 4px"
        height="20px"
        arrowColor="#000"
        position="bottom"
        content={
          <div className="h-5 w-max flex items-center">
            <KSpan text={openSidebar ? "Hide sidebar" : "Open sidebar"} fontSize={12} lineHeight="16px" color="#FFF" />
          </div>
        }
      >
        <div className="flex items-center">
          <KButton
            icon="/left-sidebar.svg"
            width="32px"
            height="32px"
            background="transparent"
            shadowDisabled={true}
            padding="6px"
            onClick={() => {
              if (setOpenSidebar) {
                setOpenSidebar(!openSidebar)
              }
            }}
          />
        </div>
      </KTooltip>
      <div>
        <KTitleSpan text={title} color="#111" fontSize={20} lineHeight="28px" />
      </div>
    </div>
  )

  const renderLeftSidebar = () => {
    return (
      <div
        className={`${
          openSidebar ? "min-w-[230px]" : "min-w-0 !w-[0px]"
        } max-w-[320px] w-[20%] bg-[#F7F7F7] min-h-[100vh] flex flex-col overflow-hidden shrink-0`}
        style={{
          transitionDuration: "0.5s"
        }}
      >
        <div className="pl-6 pr-3 py-4 whitespace-nowrap">
          <KTitleSpan text={"User Asset"} lineHeight="28px" fontSize={20} color="#111" />
        </div>

        <div className="py-1.5 px-4 h-11">
          <div
            className={`rounded-[10px] p-2 flex flex-row items-center gap-2 ${
              selectedPage === PageOptions.USERS ? "bg-[#FFF]" : "transparent"
            } cursor-pointer whitespace-nowrap`}
            onClick={() => {
              setSelectedPage(PageOptions.USERS)
              setSelectedUserTypes([])
              setPageNumber(1)
              setUserRolesOpen(!userRolesOpen)
            }}
          >
            <img src={"/reports_icons/user-circle.svg"} width={16} height={16} className="shrink-0" />
            <KSpan text={"Users"} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
          </div>
        </div>

        {selectedPage === PageOptions.USERS &&
          userRolesOpen &&
          [...userTypeOptions].map((option, index: number) => {
            const selected =
              option.value === -1 ? selectedUserTypes.length === 0 : selectedUserTypes.includes(option.value)
            return (
              <div className="py-1.5 px-12 h-11" key={`${index}`}>
                <div
                  className={`rounded-[10px] p-2 flex flex-row items-center gap-2 ${
                    selected ? "bg-[#FFF]" : "transparent"
                  } cursor-pointer whitespace-nowrap`}
                  onClick={() => {
                    if (option.value === -1) {
                      setSelectedUserTypes([])
                    } else if (selectedUserTypes.includes(option.value)) {
                      const filteredUserTypes = [...selectedUserTypes].filter(
                        (userTypeRoleId: number) => userTypeRoleId !== option.value
                      )
                      setSelectedUserTypes(filteredUserTypes)
                    } else {
                      setSelectedUserTypes([...selectedUserTypes, option.value])
                    }
                    setPageNumber(1)
                  }}
                >
                  <KSpan text={`- ${option.label}`} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
                </div>
              </div>
            )
          })}

        <div className="py-1.5 px-4 mt-4 pt-6" style={{ borderTop: "1px dashed grey" }}>
          <div
            className={`rounded-[10px] p-2 flex flex-row items-center gap-2 ${
              selectedPage === PageOptions.USER_LOGS ? "bg-[#FFF]" : "transparent"
            } cursor-pointer whitespace-nowrap`}
            onClick={() => {
              setSelectedPage(PageOptions.USER_LOGS)
              setSelectedUserTypes([])
              setPageNumber(1)
            }}
          >
            <img src={"/catalog_icons/history.svg"} width={16} height={16} className="shrink-0" />
            <KSpan text={"User Logs"} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
          </div>
        </div>

        <div className="py-1.5 px-4 h-11">
          <div
            className={`rounded-[10px] p-2 flex flex-row items-center gap-2 ${
              selectedPage === PageOptions.TAKEDOWN_LOGS ? "bg-[#FFF]" : "transparent"
            } cursor-pointer whitespace-nowrap`}
            onClick={() => {
              setSelectedPage(PageOptions.TAKEDOWN_LOGS)
              setSelectedUserTypes([])
              setPageNumber(1)
            }}
          >
            <img src={"/catalog_icons/history.svg"} width={16} height={16} className="shrink-0" />
            <KSpan text={"Takedown Logs"} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
          </div>
        </div>

        <div className="py-1.5 px-4 h-11">
          <div
            className={`rounded-[10px] p-2 flex flex-row items-center gap-2 ${
              selectedPage === PageOptions.NOTIFICATIONS ? "bg-[#FFF]" : "transparent"
            } cursor-pointer whitespace-nowrap`}
            onClick={() => {
              setSelectedPage(PageOptions.NOTIFICATIONS)
              setSelectedUserTypes([])
              setPageNumber(1)
            }}
          >
            <img src={"/catalog_icons/history.svg"} width={16} height={16} className="shrink-0" />
            <KSpan text={"Notification Logs"} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
          </div>
        </div>

        <div className="py-1.5 px-4 h-11">
          <div
            className={`rounded-[10px] p-2 flex flex-row items-center gap-2 ${
              selectedPage === PageOptions.ANNOUNCEMENTS ? "bg-[#FFF]" : "transparent"
            } cursor-pointer whitespace-nowrap`}
            onClick={() => {
              setSelectedPage(PageOptions.ANNOUNCEMENTS)
              setSelectedUserTypes([])
              setPageNumber(1)
            }}
          >
            <img src={"/catalog_icons/history.svg"} width={16} height={16} className="shrink-0" />
            <KSpan text={"Announcement Logs"} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
          </div>
        </div>
      </div>
    )
  }

  const renderUserView = () => {
    return (
      <div className="grow flex justify-center overflow-x-auto">
        <div className="w-full">
          <TableNew
            title={tableTitle("User Asset")}
            pagination
            limit={adminTableLimit}
            columns={columns}
            minRowCount={adminTableLimit}
            data={users}
            count={props.paginatedUsers.count}
            onClickPage={onClickPage}
            topRightElement={topRightElements}
            onSelectRow={
              checkAdminPermission(ADMIN_PERMISSIONS.USER_ASSET_WRITE, props.loggedInUser?.adminPermissions)
                ? (index, row) => onSelectRow(index)
                : undefined
            }
            loading={loading}
            onSortOrFilterChange={onSortOrFilterChange}
            pageNumber={pageNumber}
            additionalFilters={additionalFilters}
            onMouseEnterRow={(row: any, index: number) => {
              setHoveredRowId(index)
            }}
            onMouseLeaveRow={(row: any, index: number) => {
              if (hoveredRowId === index) {
                setHoveredRowId(undefined)
              }
            }}
            hoveredRowId={hoveredRowId}
            overrideTableOverflow="auto"
          />
          <ReloadModal
            isOpen={reloadModalOpen}
            handleClose={() => {
              setReloadModalOpen(false)
            }}
          />
          <AdminNewLabelModal open={newLabelModal} onClose={() => setNewLabelModal(false)} />
          {props.selectedUser && (
            <AdminUserCreateModal
              open={props.selectedUser !== null && addEditUserModal}
              onClose={() => {
                setAddEditUserModalOpen(false)
              }}
              isEdit={props.selectedUser?.email !== ""}
            />
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="catalog flex bg-[#fff]">
      {renderLeftSidebar()}
      {selectedPage === PageOptions.USERS && renderUserView()}
      {selectedPage === PageOptions.USER_LOGS && (
        <AdminUserLog params={props.params} setParams={props.setParams} headerDiv={tableTitle("User Logs")} />
      )}
      {selectedPage === PageOptions.TAKEDOWN_LOGS && (
        <AdminTakedownLog params={props.params} setParams={props.setParams} headerDiv={tableTitle("Takedown Logs")} />
      )}
      {selectedPage === PageOptions.NOTIFICATIONS && (
        <AdminNotificationWatch
          params={props.params}
          setParams={props.setParams}
          headerDiv={tableTitle("Notification Logs")}
        />
      )}
      {selectedPage === PageOptions.ANNOUNCEMENTS && (
        <AdminAnnouncementWatch
          params={props.params}
          setParams={props.setParams}
          headerDiv={tableTitle("Announcement Logs")}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    paginatedUsers: state.admin.paginatedUsers,
    selectedUser: state.admin.selectedUser,
    lastUpdate: state.admin.lastUpdate,
    loggedInUser: state.auth.user
  }
}

export default connect(mapStateToProps, {
  fetchAllUsers,
  selectUser,
  fetchLastUpdate,
  refreshLabelCamp,
  refreshReports,
  refreshSummaries
})(AdminUser)
